import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Sparkles, Heart, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const WELCOME_KEY = "vantastika_welcome_shown";

const WelcomeDialog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    if (!user) return;
    if (location.pathname === "/trips") return;
    const shown = localStorage.getItem(`${WELCOME_KEY}_${user.id}`);
    if (shown) return;

    // Delay longer so user sees dashboard first
    const timer = setTimeout(() => {
      const name = user.user_metadata?.display_name || user.user_metadata?.username || "";
      setDisplayName(name);
      setOpen(true);
      localStorage.setItem(`${WELCOME_KEY}_${user.id}`, "1");
    }, 2500);

    return () => clearTimeout(timer);
  }, [location.pathname, user]);

  useEffect(() => {
    if (location.pathname === "/trips" && open) {
      setOpen(false);
    }
  }, [location.pathname, open]);

  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const body = document.body;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.overflow = "hidden";

    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleStartPlanning = useCallback(() => {
    setOpen(false);
    navigate("/trips");
  }, [navigate]);

  if (!user) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/80"
            onClick={handleClose}
            aria-hidden="true"
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={t("welcome.title")}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[71] flex items-center justify-center p-4"
            style={{
              paddingTop: "calc(1rem + env(safe-area-inset-top, 0px))",
              paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))",
            }}
          >
            <div
              className="relative w-full rounded-xl border border-primary/20 bg-background shadow-lg flex flex-col"
              style={{
                maxWidth: "min(92vw, 24rem)",
                maxHeight: "min(90dvh, 600px)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleClose}
                className="absolute right-3 top-3 z-10 rounded-full p-2 opacity-70 hover:opacity-100 active:opacity-100 transition-opacity bg-background/50"
                aria-label={t("welcome.close")}
                style={{ minWidth: 44, minHeight: 44 }}
              >
                <X className="h-4 w-4" />
              </button>

              <div className="flex-1 overflow-y-auto overscroll-contain min-h-0">
                <div className="bg-gradient-to-br from-primary/15 via-primary/5 to-accent/10 px-5 pt-7 pb-3 text-center sm:px-6 sm:pt-8 sm:pb-4">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                    className="text-4xl mb-3 sm:text-5xl sm:mb-4"
                  >
                    🚐
                  </motion.div>
                  <motion.h2
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                    className="text-lg font-display font-bold sm:text-xl"
                  >
                    {displayName
                      ? t("welcome.titleName").replace("{name}", displayName)
                      : t("welcome.title")}
                  </motion.h2>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.35 }}
                  className="px-5 pb-3 space-y-4 text-center sm:px-6 sm:space-y-5"
                >
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t("welcome.desc")}
                  </p>

                  <div className="space-y-2.5 text-left sm:space-y-3">
                    {[
                      { icon: MapPin, text: t("welcome.feature1") },
                      { icon: Sparkles, text: t("welcome.feature2") },
                      { icon: Heart, text: t("welcome.feature3") },
                    ].map(({ icon: Icon, text }, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 sm:w-8 sm:h-8">
                          <Icon className="h-3.5 w-3.5 text-primary sm:h-4 sm:w-4" />
                        </div>
                        <p className="text-xs text-foreground/80 leading-snug">{text}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>

              <div className="shrink-0 border-t border-border/40 px-5 py-3 space-y-2 sm:px-6 sm:py-4">
                <Button
                  size="lg"
                  className="w-full rounded-xl font-bold shadow-glow"
                  onClick={handleStartPlanning}
                  style={{ minHeight: 48 }}
                >
                  {t("welcome.cta")}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-muted-foreground"
                  onClick={handleClose}
                  style={{ minHeight: 44 }}
                >
                  {t("welcome.dismiss")}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default WelcomeDialog;
